import { AxiosPromise } from 'axios';
import { IApiList } from '../../app/types';
import { axios } from '../axios';
import { ICreateProduct, IUpdateProduct, IProduct, IProductCategory, IProductsListFilters, IProductImage, IProductIdentifier, IProductModifier, IProductLabel } from './types';
import { getPagination, getSearchFilter, getSortFilter } from '../../app/utils';
import { I18nString } from '../i18n';

const getBase64String = (dataUri: string): string => {
  if (!dataUri) return "";
  return dataUri.includes(",") ? dataUri.split(",")[1] : dataUri;
}

const getImageParts = (image: string): { base64: string, name: string } => {
  const parts = image.split("|||");
  return {
    name: parts[0],
    base64: getBase64String(parts[1])
  }
}

const getCategoryFilter = (categories: string[]) => {
  if (categories.length === 0) {
    return "";
  }
  let query = '';
  categories.forEach(category => {
    query = `${query}&category=${category}`;
  })
  return query;
}

export const getProductsPortfolio = (
  filters: IProductsListFilters
): AxiosPromise<IApiList<IProduct>> => {
  const {
    page,
    pageSize,
    searchString: search,
    categoriesFilter: categories,
    nameFilterSort: nameSort,
    statusFilter: status,
  } = filters;
  return axios.get(`/products${getPagination(page, pageSize, true)}${getSearchFilter(search, "q")}${getCategoryFilter(categories)}${getSearchFilter(status, "statuses")}${getSortFilter(nameSort, "name")}`);
}

export const getProductCategories = (): AxiosPromise<IProductCategory[]> => {
  return axios.get(`/categories`);
}

export const getProductLabels = (): AxiosPromise<IProductLabel[]> => {
  return axios.get(`/labels`);
}

export const getProductIdentifiers = (productId: number): AxiosPromise<IProductIdentifier[]> => {
  return axios.get(`/products/${productId}/identifiers`);
}

export const addProductIdentifier = (productId: number, identifier: IProductIdentifier) => {
  return axios.post(`/products/${productId}/identifiers`, identifier);
}

export const deleteProductIdentifier = (productId: number, value: string) => {
  return axios.delete(`/products/${productId}/identifiers/${value}`);
}

export const getProductModifiers = (productId: number): AxiosPromise<IProductModifier[]> => {
  return axios.get(`/products/${productId}/modifiers`);
}

export const addProductModifier = (productId: number, modifier: IProductModifier) => {
  return axios.post(`/products/${productId}/modifiers`, modifier);
}

export const deleteProductModifiers = (productId: number, key: string) => {
  return axios.delete(`/products/${productId}/modifiers/${key}`);
}

export const createProduct = async (product: ICreateProduct): Promise<AxiosPromise<IProduct>> => {
  const response = await axios.post('/products', product);
  // if (product.image) {
  //   const imageParts = getImageParts(product.image);
  //   await axios.post(`/products/${}/images`, { image: imageParts.base64, description: imageParts.name });
  // }
  return response;
}

export const addProductSku = (productId: number, sku: { sku: string, barcodes: string[] }): AxiosPromise<IProduct> => {
  return axios.post(`/products/${productId}/skus`, sku);
}

export const addProductSkuBarcode = (skuId: string, barcode: number): AxiosPromise<IProduct> => {
  return axios.post(`/products/skus/${skuId}/barcodes`, { barcodes: [barcode] });
}

export const createCategory = (category: { name: I18nString }): AxiosPromise<IProductCategory> => {
  return axios.post('/categories', category);
}

export const createLabel = (label: { name: I18nString }): AxiosPromise<IProductLabel> => {
  return axios.post('/labels', label);
}

export const getProduct = (productId: number): AxiosPromise<IProduct> => {
  return axios.get(`/products/${productId}`);
}

export const deleteProductImages = async (productId: number, images: IProductImage[]) => {
  for (let index = 0; index < images.length; index++) {
    const image = images[index];
    await axios.delete(`/products/${productId}/images/${image.id}`);
  }
}

export const updateProduct = async (productId: number, product: Partial<IUpdateProduct>): Promise<AxiosPromise<IProduct>> => {
  if (product.image) {
    const imageParts = getImageParts(product.image);
    await axios.post(`/products/${productId}/images`, { image: imageParts.base64, description: imageParts.name });
  }
  return axios.put(`/products/${productId}`, product);
}

export const approveProducts = (ids: number[]) => {
  return axios.post('/products/approve', ids);
}

export const revertProducts = (ids: number[]) => {
  return axios.post('/products/approve?approve=false', ids);
}

export const declineProducts = (ids: number[]) => {
  return axios.post('/products/decline', ids);
}

export const archiveProducts = (ids: number[]) => {
  return axios.patch('/products/archive', ids);
}

export const unarchiveProducts = (ids: number[]) => {
  return axios.patch('/products/unarchive', ids);
}

export const deleteProduct = (productId: number) => {
  return axios.delete(`/products/${productId}`);
}

export const exportToCSV = (
  filters: IProductsListFilters
): AxiosPromise<string> => {
  const {
    searchString: search,
    categoriesFilter: categories,
    nameFilterSort: nameSort,
    statusFilter: status,
  } = filters;
  return axios.get(`/products/report/${getPagination(-1, -1, true)}${getSearchFilter(search, "skuOrName")}${getCategoryFilter(categories)}${getSearchFilter(status, "status")}${getSortFilter(nameSort, "name")}`);
}